/* eslint-disable no-underscore-dangle */
import {useInfiniteQuery as useTanstackInfiniteQuery, useMutation as useTanstackMutation, useQuery as useTanstackQuery, useQueryClient} from "@tanstack/react-query";
import deepmerge from "deepmerge";
import React, {createContext, useContext, useEffect, useRef, useState} from "react";
// import {cleanRequest} from "@vostro/clean-gql";


import {AuthStorage} from "../logic/storage";
import {cleanVariables} from "@vostro/clean-gql/lib/jtd-min";
import {useTranslation} from "react-i18next";
import {sanitizeData} from "../utils/dataFormatting";
import {requestGraphQL} from "./urql-client";
import {getOperationName} from "../utils/graphql-operation";
import {
  type CleanGQLContextValue,
  type GraphQLErrorLike,
  type GraphQLMutationResult,
  type GraphQLVariables,
  type LazyQueryRunOptions,
  type ManagerChildrenProps,
  type MutationRunOptions,
  type QueryDocWithMeta,
  type QueryHookOptions,
  type PaginatedQueryHookOptions,
  type PaginatedQueryResultLike,
  type QueryResultLike,
  type RefetchQueryInput,
  type FetchMoreOptions,
} from "../types/managers";

import {
  processDoc,
  getGqlJdtPath as getGqlJdtPathUtil,
  getWhereSchemaFromJtd,
  getModelFiltersFromSchema as getModelFiltersFromSchemaUtil,
} from "../utils/clean-gql";
import {DocumentNode} from "graphql/language/ast.js";

type LazyQueryState = {
  called: boolean;
  loading: boolean;
  data?: Record<string, unknown>;
  error?: unknown;
};

type ApolloClientQueryOptions = {
  query: QueryDocWithMeta;
  variables?: GraphQLVariables;
};

export const CleanGQLContext = createContext<CleanGQLContextValue>({});

function getRequiredJtdMeta(queryDoc: QueryDocWithMeta): unknown {
  const meta = queryDoc && queryDoc.__meta;
  // console.log("getRequiredJtdMeta", meta, queryDoc);
  if (!meta) {
    throw new Error("JTD metadata is required before executing GraphQL operations.");
  }

  return meta;
}

function normalizeVariables(
  queryDoc: QueryDocWithMeta,
  jtdSchema: unknown,
  variables?: GraphQLVariables
): GraphQLVariables {
  if (!variables) {
    return variables;
  }

  const meta = getRequiredJtdMeta(queryDoc);
  const marked = Object.fromEntries(Object.entries(variables).map(([k, v]) => [k, isPlainContainer(v) ? markNulls(v) : v]));

  return sanitizeData(unmarkNulls(cleanVariables(meta as any, jtdSchema as any, marked)));
}

const NULL_MARKER = "__clean_gql_null__";

function isPlainContainer(value: unknown): value is Record<string, unknown> | unknown[] {
  if (Array.isArray(value)) {
    return true;
  }
  if (!value || typeof value !== "object") {
    return false;
  }
  const proto = Object.getPrototypeOf(value);

  return proto === Object.prototype || proto === null;
}

function markNulls(value: unknown): unknown {
  if (value === null) {
    return NULL_MARKER;
  }
  if (Array.isArray(value)) {
    return value.map(markNulls);
  }
  if (isPlainContainer(value)) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, markNulls(v)]));
  }

  return value;
}

function unmarkNulls(value: unknown): any {
  if (value === NULL_MARKER) {
    return null;
  }
  if (Array.isArray(value)) {
    return value.map(unmarkNulls);
  }
  if (isPlainContainer(value)) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, unmarkNulls(v)]));
  }

  return value;
}

export function getGqlJdtPath() {
  return getGqlJdtPathUtil();
}


export function useCleanGQL(): CleanGQLContextValue {
  return useContext(CleanGQLContext);
}

function getRefetchOperationName(refetchQuery?: RefetchQueryInput): string | undefined {
  if (!refetchQuery) {
    return undefined;
  }

  if (typeof refetchQuery === "string") {
    return refetchQuery;
  }

  if (
    typeof refetchQuery === "object" &&
    refetchQuery &&
    "query" in refetchQuery &&
    refetchQuery.query
  ) {
    return getOperationName(refetchQuery.query as any);
  }

  return getOperationName(refetchQuery as any);
}

function normalizeGraphQLErrors(error: unknown): GraphQLErrorLike[] {
  if (!error) {
    return [];
  }

  const graphError = error as {
    graphQLErrors?: GraphQLErrorLike[];
    message?: string;
  };

  if (Array.isArray(graphError.graphQLErrors) && graphError.graphQLErrors.length > 0) {
    return graphError.graphQLErrors;
  }

  return [{
    message: graphError.message || "Unknown GraphQL error",
  }];
}

async function invalidateNamedQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  refetchQueries: RefetchQueryInput[] = []
) {
  const operationNames = refetchQueries
    .map(getRefetchOperationName)
    .filter(Boolean);

  await Promise.all(operationNames.map((operationName) => {
    return queryClient.invalidateQueries({
      predicate: (query) => {
        return Array.isArray(query.queryKey) && query.queryKey[0] === operationName;
      },
    });
  }));
}

function toQueryKey(queryDoc: QueryDocWithMeta, variables?: GraphQLVariables) {
  const operationName = getOperationName(queryDoc as any) || "anonymous";
  return [operationName, variables];
}

function processQueryDoc(query: QueryDocWithMeta, jtdSchema: unknown): QueryDocWithMeta {
  return processDoc(query as any, jtdSchema as any) as QueryDocWithMeta;
}

function mergePaginatedArrays(source: unknown[], incoming: unknown[], options?: deepmerge.Options) {
  const merged = source.slice();

  incoming.forEach((item) => {
    const nodeId = getNodeId(item);
    if (!nodeId) {
      merged.push(item);
      return;
    }

    const index = merged.findIndex((existingItem) => getNodeId(existingItem) === nodeId);
    if (index === -1) {
      merged.push(item);
      return;
    }

    merged[index] = deepmerge(
      merged[index] as Record<string, unknown>,
      item as Record<string, unknown>,
      options
    );
  });

  return merged;
}

function getNodeId(value: unknown): string | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const candidate = value as {node?: {id?: unknown}};
  return typeof candidate.node?.id === "string" ? candidate.node.id : undefined;
}

function isMergeableObject(value: unknown): boolean {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function mergePaginatedData(pages?: Array<Record<string, unknown>>): Record<string, unknown> | undefined {
  if (!pages || pages.length === 0) {
    return undefined;
  }

  return pages.reduce<Record<string, unknown> | undefined>((accumulator, page) => {
    if (!accumulator) {
      return page;
    }

    return deepmerge(accumulator, page, {
      clone: false,
      arrayMerge: mergePaginatedArrays,
      isMergeableObject: (value) => Array.isArray(value) || isMergeableObject(value),
    });
  }, undefined);
}

export function useMutation(query: QueryDocWithMeta) {
  const queryClient = useQueryClient();
  const {jtdSchema} = useCleanGQL();
  const q = processQueryDoc(query, jtdSchema);
  // getRequiredJtdMeta(q);

  const mutationResult = useTanstackMutation<GraphQLMutationResult, unknown, MutationRunOptions>({
    mutationFn: async(options: MutationRunOptions = {}) => {
      const variables = normalizeVariables(q, jtdSchema, options.variables);

      try {
        const data = await requestGraphQL(q.__cache || q, variables);
        return {
          data,
          errors: [],
        };
      } catch (error) {
        return {
          data: undefined,
          errors: normalizeGraphQLErrors(error),
          error,
        };
      }
    },
  });

  return [async(options: MutationRunOptions = {}) => {
    let opts = {
      ...options,
    };
    if (opts.variables) {
      opts.variables = normalizeVariables(q, jtdSchema, opts.variables);
    }

    const result = await mutationResult.mutateAsync(opts);

    if (opts.refetchQueries && opts.refetchQueries.length > 0) {
      await invalidateNamedQueries(queryClient, opts.refetchQueries);
    }

    return result;
  }, {
    data: mutationResult.data && mutationResult.data.data,
    error: mutationResult.error,
    loading: mutationResult.isPending,
    reset: mutationResult.reset,
  }];
}

export function useQuery(query: QueryDocWithMeta | DocumentNode, options?: QueryHookOptions): QueryResultLike {
  const {jtdSchema} = useCleanGQL();
  const q = processQueryDoc(query as unknown as any, jtdSchema);
  // getRequiredJtdMeta(q);
  const [pollInterval, setPollInterval] = useState<number | false>(false);
  const variables = options?.variables ? normalizeVariables(q, jtdSchema, options.variables) : undefined;
  const queryDoc = q.__cache;
  const queryResult = useTanstackQuery<Record<string, unknown>>({
    queryKey: toQueryKey(queryDoc as unknown as any, variables),
    queryFn: () => {
      return requestGraphQL(queryDoc, variables);
    },
    enabled: options?.skip ? false : (options?.enabled ?? true),
    refetchInterval: pollInterval,
    refetchIntervalInBackground: true,
  });

  return {
    data: queryResult.data,
    error: queryResult.error,
    loading: queryResult.isLoading,
    refetch: queryResult.refetch,
    startPolling: (intervalMs: number) => {
      setPollInterval(intervalMs || false);
    },
    stopPolling: () => {
      setPollInterval(false);
    },
    previousData: undefined,
  };
}

export function useInfiniteQuery(query: QueryDocWithMeta, options?: PaginatedQueryHookOptions): PaginatedQueryResultLike {
  const {jtdSchema} = useCleanGQL();
  const q = processQueryDoc(query, jtdSchema);
  const [pollInterval, setPollInterval] = useState<number | false>(false);
  const previousDataRef = useRef<Record<string, unknown> | undefined>(undefined);
  const nextPageVariablesRef = useRef<GraphQLVariables>(undefined);
  const variables = options?.variables ? normalizeVariables(q, jtdSchema, options.variables) : undefined;
  const queryDoc = q.__cache;
  const queryKey = [...toQueryKey(queryDoc as unknown as any, variables), "infinite"];
  const queryResult = useTanstackInfiniteQuery<Record<string, unknown>, unknown, Record<string, unknown>, typeof queryKey, GraphQLVariables>({
    queryKey,
    queryFn: ({pageParam}) => {
      return requestGraphQL(queryDoc, pageParam ?? variables);
    },
    initialPageParam: variables,
    getNextPageParam: () => {
      return nextPageVariablesRef.current;
    },
    enabled: options?.skip ? false : (options?.enabled ?? true),
    refetchInterval: pollInterval,
    refetchIntervalInBackground: true,
  });

  const mergedData = mergePaginatedData(queryResult.data?.pages as Array<Record<string, unknown>> | undefined);

  useEffect(() => {
    if (mergedData) {
      previousDataRef.current = mergedData;
    }
  }, [mergedData]);

  return {
    data: mergedData,
    error: queryResult.error,
    loading: queryResult.isLoading,
    fetchMore: async(fetchMoreOptions: FetchMoreOptions = {}) => {
      const requestedVariables = fetchMoreOptions.variables || variables;
      nextPageVariablesRef.current = requestedVariables
        ? normalizeVariables(q, jtdSchema, requestedVariables)
        : requestedVariables;

      try {
        return await queryResult.fetchNextPage();
      } finally {
        nextPageVariablesRef.current = undefined;
      }
    },
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
    refetch: queryResult.refetch,
    startPolling: (intervalMs: number) => {
      setPollInterval(intervalMs || false);
    },
    stopPolling: () => {
      setPollInterval(false);
    },
    previousData: previousDataRef.current,
  };
}


export function useLazyQuery(query: QueryDocWithMeta, options?: LazyQueryRunOptions) {
  const {jtdSchema} = useCleanGQL();
  const q = processQueryDoc(query, jtdSchema);
  // getRequiredJtdMeta(q);
  const [state, setState] = useState<LazyQueryState>({
    called: false,
    loading: false,
    data: undefined,
    error: undefined,
  });

  const run = async(opts: LazyQueryRunOptions = {}) => {
    let variables: GraphQLVariables;
    const inputVariables = opts.variables || options?.variables;
    if (inputVariables) {
      variables = normalizeVariables(q, jtdSchema, inputVariables);
    }

    setState((current) => ({
      ...current,
      called: true,
      loading: true,
      error: undefined,
    }));

    try {
      const data = await requestGraphQL(q.__cache || q, variables);
      setState({
        called: true,
        loading: false,
        data,
        error: undefined,
      });

      const onCompleted = opts.onCompleted || options?.onCompleted;
      if (onCompleted) {
        onCompleted(data);
      }

      return {data};
    } catch (error) {
      setState({
        called: true,
        loading: false,
        data: undefined,
        error,
      });

      const onError = opts.onError || options?.onError;
      if (onError) {
        onError(error);
      }

      return {
        data: undefined,
        errors: normalizeGraphQLErrors(error),
        error,
      };
    }
  };

  return [run, {
    called: state.called,
    loading: state.loading,
    data: state.data,
    error: state.error,
    refetch: run,
  }];
}

export function useApolloClientQuery() {
  const {jtdSchema} = useCleanGQL();
  return [async(opts: ApolloClientQueryOptions) => {
    if (!opts?.query) {
      return {
        data: undefined,
        errors: [{message: "Missing query"}],
      };
    }
    const q = processQueryDoc(opts.query, jtdSchema);
    // getRequiredJtdMeta(q);
    const variables = opts?.variables ? normalizeVariables(q, jtdSchema, opts.variables) : undefined;

    try {
      const data = await requestGraphQL(q.__cache || q, variables);
      return {
        data,
        errors: [],
      };
    } catch (error) {
      return {
        data: undefined,
        errors: normalizeGraphQLErrors(error),
        error,
      };
    }
  }];
}

export default function CleanGqlProvider(props: ManagerChildrenProps) {
  const [jtdSchema, setJtdSchema] = useState<CleanGQLContextValue["jtdSchema"]>();
  async function getJtdSchema() {
    let options: RequestInit = {};
    const token = AuthStorage.get("token");
    if (token && token !== "") {
      options = {
        headers: {
          authorization: token ? `Bearer ${String(token)}` : "",
        },
      };
    } else {
      options = {
        credentials: "include",
      };
    }
    const response = await fetch(getGqlJdtPath(), options);
    const jt = await response.json();
    setJtdSchema(jt);
  }
  useEffect(() => {
    getJtdSchema();
  }, []);
  if (!jtdSchema) {
    return (<React.Fragment />);
  }
  return (<CleanGQLContext.Provider value={{jtdSchema}}>
    {props.children}
  </CleanGQLContext.Provider>);
}


export function getEnum(name: string) {
  const {t} = useTranslation();
  const {jtdSchema} = useCleanGQL();
  const enums = jtdSchema?.def && Object.prototype.hasOwnProperty.call(jtdSchema.def, name)
    ? (jtdSchema.def[name] as {enum?: string[]; em?: string[]})?.enum || jtdSchema.def[name]?.em || []
    : [];
  return enums.map((e) => {
    const translationName = `resource.enum.${e}`;
    const text = t(translationName);
    return {
      value: e,
      text: text === translationName ? e : text,
    };
  });
}

export function getWhereSchema(modelName: string) {
  const {jtdSchema} = useCleanGQL();
  return getWhereSchemaFromJtd(jtdSchema ?? {}, modelName);
}

export function getModelFiltersFromSchema(schema: unknown) {
  return getModelFiltersFromSchemaUtil(schema);
}
